<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { t } from "@/i18n";

const displaySentences = computed(() => [
    t("shared.ai_generation_texts.assembling_pixels"),
    t("shared.ai_generation_texts.convincing_robots"),
    t("shared.ai_generation_texts.bribing_ai"),
    t("shared.ai_generation_texts.teaching_ai_colors"),
    t("shared.ai_generation_texts.unfolding_space_time"),
    t("shared.ai_generation_texts.no_comic_sans"),
    t("shared.ai_generation_texts.spinning_genius"),
    t("shared.ai_generation_texts.ai_button_size"),
    t("shared.ai_generation_texts.dreaming_site"),
    t("shared.ai_generation_texts.training_ai_intern"),
    t("shared.ai_generation_texts.negotiating_fonts"),
    t("shared.ai_generation_texts.ai_takes_wheel"),
    t("shared.ai_generation_texts.coffee_to_code"),
    t("shared.ai_generation_texts.debugging_humor"),
    t("shared.ai_generation_texts.aligning_divs"),
    t("shared.ai_generation_texts.loading_creativity"),
    t("shared.ai_generation_texts.picking_color"),
    t("shared.ai_generation_texts.uploading_duct_tape"),
    t("shared.ai_generation_texts.whispering_patterns"),
]);

const MAX_DOTS = 6;
const currentSentenceIndex = ref(0);
const dotCount = ref(3);
const usedIndices = ref<number[]>([]);
const renderDots = computed(() => ".".repeat(dotCount.value));
const currentPhase = ref("showing");

const currentSentence = computed(() => displaySentences.value[currentSentenceIndex.value]);
const allSentenceIndexes = displaySentences.value.map((_, index) => index);

function getRandomIndex() {
    const availableIndices = allSentenceIndexes.filter(
        (index) => !usedIndices.value.includes(index)
    );

    if (availableIndices.length === 0) {
        usedIndices.value = [];
        return Math.floor(Math.random() * displaySentences.value.length);
    }

    return availableIndices[Math.floor(Math.random() * availableIndices.length)];
}

onMounted(() => {
    const dotInterval = setInterval(() => {
        dotCount.value = (dotCount.value + 1) % MAX_DOTS;
    }, 500);

    const sentenceInterval = setInterval(() => {
        currentPhase.value = "transitioning";
        usedIndices.value.push(currentSentenceIndex.value);
        const nextIndex = getRandomIndex();
        setTimeout(() => {
            currentSentenceIndex.value = nextIndex;
            currentPhase.value = "showing";
        }, 400);
    }, 4000);

    onUnmounted(() => {
        clearInterval(dotInterval);
        clearInterval(sentenceInterval);
    });
});
</script>

<template>
    <div
        class="w-full h-screen fixed flex items-center justify-center overflow-hidden ct-bg-1 z-30 inset-0"
        style="perspective: 1000px"
    >
        <div
            class="absolute w-full flex items-center justify-center transition-all duration-400"
            :class="{
                'opacity-100 transform rotate-0 translate-y-0': currentPhase === 'showing',
                'opacity-0 transform -rotate-x-90 -translate-y-4': currentPhase === 'transitioning',
            }"
            style="
                transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
                transform-style: preserve-3d;
            "
        >
            <p class="text-2xl font-medium ct-text-1 whitespace-nowrap">
                {{ currentSentence }}
                <span class="inline-block w-8 text-left">{{ renderDots }}</span>
            </p>
        </div>
    </div>
</template>
